import React, { useEffect, useState } from 'react'
import { Button, Message } from 'semantic-ui-react'
import { createActivity } from 'utility/services/crm'
import { toApiError } from 'utility/crm/api-error'
import { EMAIL_TEMPLATES, EMAIL_TEMPLATE_KEYS, buildMailto, fillTemplate } from 'utility/crm/email-templates'
import { useAuth } from 'context/user'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmToast } from 'hooks/crm'
import { CrmModal } from './CrmOverlay'
import { Muted } from './ui'

const label = { display: 'block', fontWeight: 600, margin: '10px 0 4px' }
const field = { width: '100%', padding: 8 }

/**
 * Email a contact from a template. "Open in mail app" opens mailto: with the text filled in; the email is
 * only logged on the timeline when the user confirms with "Log as sent".
 * @param {{ contact: import('utility/crm/types').CrmContact|null, onClose: () => void }} props
 */
export const EmailDialog = ({ contact, onClose }) => {
    const { user } = useAuth()
    const { successToast } = useCrmToast()
    const [template, setTemplate] = useState('intro')
    const [to, setTo] = useState('')
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState(null)

    const apply = key => {
        const filled = fillTemplate(key, { contactName: contact?.Name, company: contact?.AccountName, me: user?.FullName?.trim() })
        setSubject(filled.subject)
        setBody(filled.body)
    }

    useEffect(() => {
        if (!contact) return
        setTemplate('intro')
        setTo(contact.Email || '')
        setError(null)
        apply('intro')
    }, [contact?.ContactId]) // eslint-disable-line

    const logAsSent = async () => {
        setBusy(true)
        setError(null)
        try {
            await createActivity({ Type: 'email', ContactId: contact.ContactId, Title: subject.trim() || EMAIL_TEMPLATES[template].name, Body: body })
            successToast('Email logged')
            invalidateCrm(...ALL_CRM_SCOPES)
            onClose()
        } catch (err) {
            setError(toApiError(err).message)
        } finally {
            setBusy(false)
        }
    }

    return (
        <CrmModal
            open={Boolean(contact)}
            onClose={busy ? () => {} : onClose}
            title={contact ? `Email · ${contact.Name}` : 'Email'}
            width={600}
            footer={
                <>
                    <a
                        className="ui button"
                        href={buildMailto({ to, subject, body })}
                        style={{ marginRight: 'auto' }}
                        aria-disabled={!to.trim()}
                        onClick={event => {
                            if (!to.trim()) event.preventDefault()
                        }}
                    >
                        Open in mail app
                    </a>
                    <Button type="button" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button type="button" primary onClick={logAsSent} loading={busy} disabled={busy}>
                        Log as sent
                    </Button>
                </>
            }
        >
            {error ? (
                <Message negative role="alert">
                    {error}
                </Message>
            ) : null}
            <label htmlFor="mail-template" style={label}>
                Template
            </label>
            <select
                id="mail-template"
                value={template}
                onChange={e => {
                    setTemplate(e.target.value)
                    apply(e.target.value)
                }}
                style={field}
            >
                {EMAIL_TEMPLATE_KEYS.map(key => (
                    <option key={key} value={key}>
                        {EMAIL_TEMPLATES[key].name}
                    </option>
                ))}
            </select>

            <label htmlFor="mail-to" style={label}>
                To
            </label>
            <input id="mail-to" type="email" value={to} onChange={e => setTo(e.target.value)} style={field} />
            {!to.trim() ? (
                <p role="status" style={{ color: '#b45309', margin: '2px 0', fontSize: '0.8rem' }}>
                    This contact has no email address. Add one to open your mail app.
                </p>
            ) : null}

            <label htmlFor="mail-subject" style={label}>
                Subject
            </label>
            <input id="mail-subject" value={subject} onChange={e => setSubject(e.target.value)} style={field} maxLength={200} />

            <label htmlFor="mail-body" style={label}>
                Message
            </label>
            <textarea id="mail-body" rows={9} value={body} onChange={e => setBody(e.target.value)} style={field} />
            <Muted style={{ display: 'block', marginTop: 6 }}>
                Opening your mail app does not record anything. Use “Log as sent” once you have sent it.
            </Muted>
        </CrmModal>
    )
}
